import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { StudentType } from '../model/model'
import { setViewStudent } from './configSlice'
import { RootState } from './store'
import { resetUser } from './userSlice'

const sliceName = 'student'

type SliceStateType = {
  fetching: boolean
  fetchingSingleStudent: boolean
  students: Record<string, StudentType>
}

const initialState: SliceStateType = {
  fetching: false,
  fetchingSingleStudent: false,
  students: {}
}

type StudentsRequestType = {
  filters: {
    term: string
  }
}

export type StudentsResponseType = Array<StudentType>

export const fetchStudents = createAsyncThunk<StudentsResponseType, StudentsRequestType>(
  `${sliceName}/students`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}pagedStudent`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(args),
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        }
      }).then((resp) => resp.json())

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

export const fetchSingleStudent = createAsyncThunk<StudentType, { id: number; setViewStudent?: boolean }>(
  `${sliceName}/singleStudent`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response: StudentType = await fetch(`${state.setting.root}student/${args.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        }
      }).then((resp) => resp.json())

      if (args.setViewStudent) {
        thunkAPI.dispatch(resetUser())

        thunkAPI.dispatch(
          setViewStudent({
            rank: Number(response.rank),
            studentID: response.id.toString(),
            studentName: response.name
          })
        )
      }

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  },
  {
    condition: (_args, thunkAPI) => {
      const state = (thunkAPI.getState() as RootState)[sliceName]

      if (state.fetchingSingleStudent) {
        return false
      }

      return true
    }
  }
)

export const studentSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchStudents.pending, (state) => {
      state.fetching = true
    })

    builder.addCase(fetchStudents.fulfilled, (state, action) => {
      state.fetching = false

      const data = action.payload.reduce((result, obj) => {
        result[obj.id] = obj

        return result
      }, {})

      state.students = {
        ...state.students,
        ...data
      }
    })

    builder.addCase(fetchSingleStudent.pending, (state) => {
      state.fetchingSingleStudent = true
    })

    builder.addCase(fetchSingleStudent.fulfilled, (state, action) => {
      state.fetchingSingleStudent = false
      state.students[action.payload.id] = action.payload
    })
  }
})
